import { createSlice, isAnyOf, type PayloadAction } from "@reduxjs/toolkit";
import { getPlatformFieldsAsync } from "../actions/productActions";

interface CustomFieldsState {
  customFields: any[];
  customPage: number;
  customHasMore: boolean;
  customLoading: boolean;
}

const initialState: CustomFieldsState = {
  customFields: [],
  customPage: 1,
  customHasMore: true,
  customLoading: false,
};

type PlatformFieldsRequestArg = {
  platformId: number;
  otherParams?: Record<string, unknown>;
};

/** Title/description CUSTOM_FIELDS dropdown — paginated source-store product custom fields only. */
export function isTitleDescriptionCustomFieldsRequest(
  arg: PlatformFieldsRequestArg | undefined
): boolean {
  const params = arg?.otherParams;
  if (!params) return false;
  return (
    params.field_type === "CUSTOM" &&
    params.owner === "PRODUCT" &&
    params.includeLocalFields === false &&
    Number(params.limit) === 100
  );
}

function normalizePlatformFieldsPayload(payload: unknown): any[] {
  if (Array.isArray(payload)) return payload;
  if (
    payload &&
    typeof payload === "object" &&
    Array.isArray((payload as { results?: unknown }).results)
  ) {
    return (payload as { results: any[] }).results;
  }
  return [];
}

const customFieldsSlice = createSlice({
  name: "customFields",
  initialState,
  reducers: {
    resetCustomFields: (state) => {
      state.customFields = [];
      state.customPage = 1;
      state.customHasMore = true;
      state.customLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(isAnyOf(getPlatformFieldsAsync.pending), (state, action) => {
      if (!isTitleDescriptionCustomFieldsRequest(action.meta.arg)) return;
      state.customLoading = true;
    });

    builder.addMatcher(isAnyOf(getPlatformFieldsAsync.rejected), (state, action) => {
      if (!isTitleDescriptionCustomFieldsRequest(action.meta.arg)) return;
      state.customLoading = false;
    });

    builder.addMatcher(
      isAnyOf(getPlatformFieldsAsync.fulfilled),
      (state, action: PayloadAction<unknown, string, { arg: PlatformFieldsRequestArg }>) => {
        if (!isTitleDescriptionCustomFieldsRequest(action.meta.arg)) return;

        const results = normalizePlatformFieldsPayload(action.payload);
        const requestedPage = Number(action.meta.arg.otherParams?.page) || 1;

        state.customLoading = false;

        if (results.length === 0) {
          if (requestedPage <= 1) {
            state.customFields = [];
            state.customPage = 1;
          }
          state.customHasMore = false;
          return;
        }

        if (requestedPage <= 1) {
          state.customFields = results;
          state.customPage = 1;
          state.customHasMore = results.length >= 100;
          return;
        }

        const existingIds = new Set(state.customFields.map((f) => f.id));
        const newFields = results.filter((f) => !existingIds.has(f.id));

        if (newFields.length === 0) {
          state.customHasMore = false;
          return;
        }

        state.customFields = [...state.customFields, ...newFields];
        state.customPage = requestedPage;
        state.customHasMore = results.length >= 100;
      }
    );
  },
});

export const { resetCustomFields } = customFieldsSlice.actions;
export default customFieldsSlice.reducer;
